import { cssVar, Flex, Icon, Box, Text } from '@cad-arc';
import { observer } from 'mobx-react-lite';
import { useEffect, useRef } from 'react';
import { HighlightedText } from '../../../common/components/highlighted-text';
import { formatFullAddress } from '../../helpers/location-display';
import { getMapKey } from '../../../esri/helpers/utils';
import { LocationStore } from '../../location-store';
import { LocationStoreView } from '../../types/locations';
import { CommonSize, getBasicCommonSize } from '../../../common/helpers/utils';

export const LocationMapListItem = observer(function MapListItem(props: {
    location: LocationStoreView;
    index: number;
    store: LocationStore;
    query: string;
    onClickRow: ((id: number) => void) | undefined;
    onLocationSelected: (
        location: LocationStoreView,
        index: number,
        source: { source: 'keyboard' | 'mouse' }
    ) => void;
    moveFocusNext?: () => void;
    inputRef?: React.MutableRefObject<HTMLInputElement | null>;
    showMapKey?: boolean;
    size?: CommonSize;
}) {
    const {
        location,
        index,
        query,
        store,
        onClickRow,
        onLocationSelected,
        moveFocusNext,
        inputRef,
        showMapKey = true,
        size,
    } = props;
    const { isForcedResult } = location;
    const ref = useRef<HTMLDivElement | null>(null);
    useEffect(() => {
        if (
            ref.current &&
            store.highlightedLocationIndex === index &&
            store.shouldScrollLocationList
        ) {
            ref.current.scrollIntoView(false);
        }
    }, [store.highlightedLocationIndex, index, store.shouldScrollLocationList]);

    const isSelected = store.highlightedLocationIndex === index

    return (
        <Box
            onClick={() => {
                if (onClickRow) {
                    onClickRow(location.id);
                }
                inputRef?.current?.focus?.();
                moveFocusNext?.();
                store.setSelectedLocation(location);
            }}
            key={location.id}
            paddingX={3}
            paddingY={1}
            onMouseOver={() => {
                onLocationSelected(location, index, { source: 'mouse' });
            }}
            _hover={{ backgroundColor: 'brand.hover', color: 'brand.content' }}
            color={isForcedResult ? 'text.secondary' : undefined}
            bg={isSelected ? 'brand.accent' : ''}
            ref={ref}
            cursor="default"
        >
            <Flex
                alignItems="center"
                fontFamily="jaf-facitweb, monospace"
                justifyContent="space-between"
            >
                <Flex alignItems="center" cursor="default">
                    {!isForcedResult && showMapKey && (
                        <Flex
                            borderRadius="2px"
                            h="25px"
                            bg="brand.default"
                            minWidth="25px"
                            paddingX={2}
                            width="fit-content"
                            alignItems="center"
                            justifyContent="center"
                        >
                            <Text fontSize={size} casing="uppercase" style={{color:cssVar("arc.colors.brand.content")}}>{getMapKey(index)}</Text>
                        </Flex>
                    )}
                    <Box
                        ml={isForcedResult ? 'calc(var(--arc-space-2) + 25px)' : 2}
                    >
                        {isForcedResult ? (
                            <Text noOfLines={1} fontSize={size} color="inherit">
                                {formatFullAddress(location)}
                            </Text>
                        ) : (
                            <HighlightedText matchPattern={query} noOfLines={1} fontSize={size} color="inherit">
                                {formatFullAddress(location)}
                            </HighlightedText>
                        )}
                    </Box>
                </Flex>
                <Icon icon="Location" size={getBasicCommonSize(size)} />
            </Flex>
        </Box>
    );
});
